/* CUSTOM countdown-clock — EINE Countdown-Logik für alle Flächen (Folke 2026-07-15).
   Vorher lag die Datumsmathematik doppelt (countdown-reverse + link-hub) und mit fest
   verdrahtetem Enddatum: lief es ab, verschwand der Countdown einfach (der Kollektions-
   Countdown stand seit April auf einem toten Datum). Deshalb hier zwei Modi:

     data-recur-day="15"   → nächster 15. des Monats, rollt automatisch weiter. Nie tot.
     data-end="2026-08-14T18:00:00+02:00" → einmaliges Datum (z.B. ein Drop).

   Markup:
     <countdown-clock data-recur-day="15" data-time="23:59" data-seconds="true">
       <span data-d></span><span data-h></span><span data-m></span><span data-s></span>
     </countdown-clock>

   Ohne JS bleibt der Serverwert stehen; fällt das Ziel weg, setzt das Element data-expired
   und die Fläche kann sich per CSS selbst regeln (statt hart display:none von innen).
*/
/* Remote-Modus (Folke/Coder 2026-08-04, #814 §2):
   Setzt man data-remote-target="drop|closing|sale", holt der Timer sein Ziel live vom
   Studiolog-Endpunkt termineData (Präsenz ist SSOT, 5 min Server-Cache). Fällt der Fetch
   aus oder ist das Feld null, greifen die im Element schon gesetzten Attribute (data-end
   oder data-recur-day) als Fallback — kein leerer Countdown.
   Sale-Sonderfall: `ziel` beim Sale ist das ENDE (Coder-Note); für Countdown "bis Sale"
   wollen wir den Start → bauen datum + von + Zone selbst. */
const TERMINE_URL = 'https://europe-west1-studiolog-13-5.cloudfunctions.net/termineData';
let _terminePromise = null;
function loadTermine() {
  if (!_terminePromise) {
    _terminePromise = fetch(TERMINE_URL, { credentials: 'omit' }).then(r => r.json()).catch(() => null);
  }
  return _terminePromise;
}
const REMOTE_MAP = { drop: 'naechsterDrop', closing: 'naechstesClosing', sale: 'naechsterSale' };

class CountdownClock extends HTMLElement {
  connectedCallback() {
    this.d = this.querySelector('[data-d]');
    this.h = this.querySelector('[data-h]');
    this.m = this.querySelector('[data-m]');
    this.s = this.querySelector('[data-s]');
    this.dateEl = this.querySelector('[data-date]');
    this.timeEl = this.querySelector('[data-target-time]');
    const remote = this.dataset.remoteTarget;
    if (remote && REMOTE_MAP[remote]) {
      loadTermine().then(data => {
        const item = data && data[REMOTE_MAP[remote]];
        if (!item) return;
        let iso = null;
        if (remote === 'sale' && item.datum && item.von) {
          const zone = (item.ziel || '').slice(-6) || '+02:00';
          iso = item.datum + 'T' + item.von + ':00' + zone;
        } else if (item.ziel) {
          iso = item.ziel;
        }
        if (!iso) return;
        this.dataset.end = iso;
        delete this.dataset.recurDay;
        delete this.dataset.notBefore;
      }).finally(() => this.tick());
    } else {
      this.tick();
    }
  }

  /* Zieldatum als DD.MM.YYYY — wird aus demselben target() abgeleitet wie die Uhr.
     Bewusst NICHT als zweites Feld im Text gepflegt: genau diese Dopplung (Datum im Fließtext
     neben Datum in den Daten) hat am 15.07. den Workshop-Kalender auseinanderlaufen lassen. */
  fillDate(t) {
    if (!this.dateEl || !t) return;
    const p = (n) => String(n).padStart(2, '0');
    this.dateEl.textContent = `${p(t.getDate())}.${p(t.getMonth() + 1)}.${t.getFullYear()}`;
    if (this.timeEl) this.timeEl.textContent = `${p(t.getHours())}:${p(t.getMinutes())}`;
  }

  disconnectedCallback() { clearTimeout(this._t); }

  /* Nächstes Ziel. Bei Wiederholung: der Tag im laufenden Monat, sonst im nächsten.
     Monatslängen werden gekappt (Tag 31 im Februar → letzter Tag), damit nichts überläuft.

     data-not-before: erster gültiger Termin. Ohne das würde der Rhythmus schon im laufenden
     Monat feuern — am 15.07. hätte „jeden 15." behauptet, die Charge schließe heute Nacht,
     obwohl der erste Schluss der 15.08. ist. Ist der erste Termin durch, greift der Rhythmus. */
  target() {
    const nb = this.dataset.notBefore ? new Date(this.dataset.notBefore) : null;
    if (nb && !isNaN(nb.getTime()) && new Date() < nb) return nb;

    const day = parseInt(this.dataset.recurDay || '0', 10);
    if (day > 0) {
      const parts = (this.dataset.time || '23:59').split(':');
      const hh = parseInt(parts[0], 10) || 0, mm = parseInt(parts[1], 10) || 0;
      const now = new Date();
      const build = (y, mo) => {
        const last = new Date(y, mo + 1, 0).getDate();
        return new Date(y, mo, Math.min(day, last), hh, mm, 0, 0);
      };
      let t = build(now.getFullYear(), now.getMonth());
      if (t <= now) t = build(now.getFullYear(), now.getMonth() + 1);
      return t;
    }
    const end = this.dataset.end && new Date(this.dataset.end);
    return end && !isNaN(end.getTime()) ? end : null;
  }

  tick() {
    const t = this.target();
    if (!t) { this.setAttribute('data-expired', ''); return; }
    this.fillDate(t);
    let diff = t - new Date();
    if (diff <= 0) {
      // Bei Wiederholung nie abgelaufen — target() liefert dann schon den nächsten Termin.
      this.setAttribute('data-expired', '');
      return;
    }
    this.removeAttribute('data-expired');
    const total = Math.floor(diff / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    if (this.d) this.d.textContent = Math.floor(total / 86400);
    if (this.h) this.h.textContent = pad(Math.floor((total % 86400) / 3600));
    if (this.m) this.m.textContent = pad(Math.floor((total % 3600) / 60));
    if (this.s) this.s.textContent = pad(total % 60);
    // Mit Sekunden sekündlich, sonst minütlich — kein unnötiger Timer auf Mobilgeräten.
    const next = this.s ? 1000 - (Date.now() % 1000) : 60000;
    this._t = setTimeout(() => this.tick(), next);
  }
}
if (!customElements.get('countdown-clock')) customElements.define('countdown-clock', CountdownClock);
